import React, { useEffect, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { DropdownDots, SortIcon, ToggleIconLeft } from "../../components/ui/icons";
import { IColumns, IListFirmwares } from "../../common/types/firmwareTypes";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { Action } from "redux";
import { useDispatch, useSelector } from "react-redux";
import {
  selector as listFirmwareApi,
  fetchFirmwareData,
} from "./firmwareSlice";
import { useSpinner } from "../../customHooks";
import { IListIotAgent } from "../../common/types/iotAgentTypes";
import { selector as listIotAgentApi, fetchIotData } from "./iotAgentSlice";
import useNotification from "../../components/notification/useNotification";
import { isSuccessResponse } from "../../common/utils";
import { ISuccsessResponse } from "../../common/types/uiTypes";
import { AxiosError } from "axios";
import { Status } from "../../common/constants";
import {
  uploadFirmware,
  selector as uploadFirmwareApi,
} from "./uploadFirmwareSlice";
import {
  uploadIotAgent,
  selector as uploadIotAgentApi,
} from "./uploadIotAgentSlice";
import { Dropdown } from "react-bootstrap";
import DeleteDeviceExecutablesModal from "./deleteDeviceExecutables";
interface IUploadFirmwareResponse {
  message: string;
  status: string;
}
const useManageUpdates = (isFirmwareUpload: Boolean) => {
  const dispatch: ThunkDispatch<IUploadFirmwareResponse, undefined, Action> =
    useDispatch();
  const dispatchFirmwares: ThunkDispatch<IListFirmwares, undefined, Action> =
    useDispatch();
  const dispatchIotAgents: ThunkDispatch<IListIotAgent, undefined, Action> =
    useDispatch();
  const listFirmwaresResponse: IListFirmwares = useSelector(listFirmwareApi);
  const listIotAgentsResponse: IListIotAgent = useSelector(listIotAgentApi);
  const uploadFirmwareResponse = useSelector(uploadFirmwareApi);
  const uploadIotAgentResponse = useSelector(uploadIotAgentApi);

  const status: string =
    listFirmwaresResponse.status === Status.PENDING
      ? listFirmwaresResponse.status
      : listIotAgentsResponse.status === Status.PENDING
      ? listIotAgentsResponse.status
      : uploadFirmwareResponse.status === Status.PENDING
      ? uploadFirmwareResponse.status
      : uploadIotAgentResponse.status;
  useSpinner(status);

  const [listFirmwaresData, setFirmwareListData] = useState<IColumns | null>();
  const [listIotAgentsData, setIotAgentListData] = useState<IColumns | null>();
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const allowedFileTypes = [
    "application/zip",
    "application/x-zip-compressed",
    "",
  ];
  const maxSizeInBytes = 20 * 1024 * 1024;
  const columns: ColumnDef<IColumns, string>[] = React.useMemo(
    () => [
      {
        accessorFn: (row) => `${row?.name}`,
        accessorKey: "name",
        header: (row) => (
          <div className="table-heading">
            Name{" "}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => `${row?.version}`,
        accessorKey: "version",
        header: (row) => (
          <div className="table-heading">
            Version{" "}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => <div className="table-cell">{row.getValue()}</div>,
      },
      {
        accessorFn: (row) => `${row?.timestamp}`,
        accessorKey: "timestamp",
        header: (row) => (
          <div className="table-heading">
            Uploaded On{" "}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => (
          <div className="table-cell">
            {new Date(row.getValue()).toLocaleString()}
          </div>
        ),
      },
      {
        accessorFn: (row) => `${row?.fileDescription}`,
        accessorKey: "fileDescription",
        header: (row) => (
          <div className="table-heading">
            File Description{" "}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => (
          <div className="table-cell">{row.getValue() != "undefined" ? row.getValue() : "N/A"}</div>
        )        
      },
      {
        accessorKey: "visits",
        header: () => <span></span>,
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        Cell: () => <ToggleIconLeft />,
        cell: (row) => {
          return (<div className='actions-btns' >
            <Dropdown>
              <Dropdown.Toggle id='dropdowndots'>
                <DropdownDots />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                  <Dropdown.Item href=''>
                  <DeleteDeviceExecutablesModal
                    name={row.row.original.name}
                    version={row.row.original.version}
                    isFirmware={isFirmwareUpload}
                  />
                  </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div >)
        }
      },
    ],
    []
  );
  const { showSuccessNotification, showWarningNotification } =
    useNotification();

    const handleUploadButtonClick = async (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      isFirmwareUpload: boolean
    ): Promise<void> => {
      if (!selectedFile) {
        showWarningNotification("Please select a file.");
        return;
      }
      const file: File = selectedFile;
      const fileName: string = file.name;
      const arrayBuffer: ArrayBuffer = await file.arrayBuffer();
      const payload: {
        file: ArrayBuffer;
        name: string;
      } = { file: arrayBuffer, name: fileName };
      const uploadFile = isFirmwareUpload ? uploadFirmware : uploadIotAgent;
    
      dispatch(uploadFile(payload)).then((response) => {
        const uploadFileResponse = response.payload as
          | ISuccsessResponse
          | AxiosError;
        if (isSuccessResponse(uploadFileResponse)) {
          dispatchFirmwares(fetchFirmwareData());
          dispatchIotAgents(fetchIotData());
          showSuccessNotification(uploadFileResponse.message);
        } else {
          showWarningNotification(uploadFileResponse.response?.data.message);
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      return;
      });
    };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, isFirmwareUpload: boolean) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      if (!allowedFileTypes.includes(file.type)) {
        setErrorMessage("Invalid file type. Only zip files are allowed.");
        setSelectedFile(null);
        return;
      }
      if (file.size > maxSizeInBytes) {
        setErrorMessage(
          `File size exceeds the limit of ${maxSizeInBytes / (1024 * 1024)} MB.`
        );
        setSelectedFile(null);
        return;
      }
      const fileNamePattern: RegExp = isFirmwareUpload
        ? /^Firmware_v\d+(\.\d+)*\.zip$/
        : /^IOTAgent_v\d+(\.\d+)*$/;
      const errorMessage: string = isFirmwareUpload
        ? 'File name must follow the format "Firmware_v{version}" (e.g., Firmware_v1, Firmware_v2.1).'
        : 'File name must follow the format "IOTAgent_v{version}" (e.g., IOTAgent_v1, IOTAgent_v2.1).';
      if (!fileNamePattern.test(file.name)) {
        setErrorMessage(errorMessage);
        setSelectedFile(null);
        return;
      }
    }
      setSelectedFile(file);
      setErrorMessage(null);
  };

  useEffect(() => {
    if (listFirmwaresResponse && listFirmwaresResponse.values) {
      setFirmwareListData(listFirmwaresResponse.values);
    }
    if (listIotAgentsResponse && listIotAgentsResponse.values) {
      setIotAgentListData(listIotAgentsResponse.values);
    }
  }, [listFirmwaresResponse, listIotAgentsResponse]);

  useEffect(() => {
    if (listFirmwaresResponse.status !== Status.SUCCESS) {
      dispatchFirmwares(fetchFirmwareData());
    }
    if (listIotAgentsResponse.status !== Status.SUCCESS) {
      dispatchIotAgents(fetchIotData());
    }
  }, [dispatchFirmwares, dispatchIotAgents]);

  return {
    columns,
    listFirmwaresData,
    listIotAgentsData,
    handleUploadButtonClick,
    handleFileChange,
    errorMessage,
    fileInputRef,
    selectedFile,
  };
};

export default useManageUpdates;
